import { useEffect, useState } from 'react';
import type { ChartEvent } from '@/components/charts/LineChart';
import type { Point } from '@/components/charts/scale';

/*
  Примерные данные приложения-образца «Инфраструктура» (как на витрине и в «Характере»).
  Генераторы детерминированы: одна и та же картинка при каждом открытии.
*/

export function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Значение, которое само шевелится: имитация живого опроса. */
export function useDrift(base: number, spread: number, live: boolean, periodMs: number) {
  const [v, setV] = useState(base);
  useEffect(() => {
    if (!live) return;
    const id = window.setInterval(() => {
      setV((prev) =>
        Math.max(base - spread * 2, Math.min(base + spread * 2, prev + (Math.random() - 0.5) * spread)),
      );
    }, periodMs);
    return () => window.clearInterval(id);
  }, [base, spread, live, periodMs]);
  return v;
}

/** Диск за 30 дней и прогноз на 12 дней вперёд (R30). */
export function diskWithForecast(now: number): { points: Point[]; forecast: Point[] } {
  const rnd = mulberry32(19);
  const day = 24 * 3600 * 1000;
  const points: Point[] = [];
  for (let i = 30; i >= 0; i--) {
    points.push({ x: now - i * day, y: Math.round((77 - i * 1.2 + (rnd() - 0.5) * 1.2) * 10) / 10 });
  }
  const forecast: Point[] = [];
  for (let i = 1; i <= 12; i++) forecast.push({ x: now + i * day, y: Math.round((77 + i * 1.25) * 10) / 10 });
  return { points, forecast };
}

/** Процессор за 12 часов со ступенькой после выкладки и засечками событий (R29). */
export function cpuWithEvents(now: number): { points: Point[]; events: ChartEvent[] } {
  const rnd = mulberry32(7);
  const step = 10 * 60 * 1000;
  const start = now - 12 * 3600 * 1000;
  const deployAt = start + 5 * 3600 * 1000;
  const migrationAt = start + 8.5 * 3600 * 1000;
  const rebootAt = start + 10.5 * 3600 * 1000;
  const points: Point[] = [];
  for (let t = start; t <= now; t += step) {
    const base = 22 + 4 * Math.sin((t - start) / (2.5 * 3600 * 1000));
    const v = base + (t >= deployAt ? 26 : 0) + (t >= rebootAt ? -14 : 0) + (rnd() - 0.5) * 3;
    points.push({ x: t, y: Math.round(Math.max(4, v) * 10) / 10 });
  }
  return {
    points,
    events: [
      { x: deployAt, kind: 'deploy', label: 'Выкладка 1.14.2, агент метрик' },
      { x: migrationAt, kind: 'migration', label: 'Переезд двух машин с host-341' },
      { x: rebootAt, kind: 'reboot', label: 'Перезагрузка после обновления ядра' },
    ],
  };
}

export interface NodeRow {
  id: string;
  role: string;
  cpu: number;
  cpuTrend: number[];
  mem: number;
  /** Инцидентов за сутки: зарубка у строки (строка помнит, что с ней было). */
  incidents: number;
  state: 'Проблема' | 'Норма';
}

export const NODES: NodeRow[] = [
  {
    id: 'host-338',
    role: 'Площадка А · R-A',
    cpu: 34,
    cpuTrend: [22, 24, 23, 27, 31, 29, 33, 58, 74, 52, 39, 34],
    mem: 61,
    incidents: 2,
    state: 'Проблема',
  },
  {
    id: 'host-341',
    role: 'Площадка А · R-A',
    cpu: 18,
    cpuTrend: [14, 15, 16, 15, 17, 16, 18, 17, 19, 18, 18, 18],
    mem: 44,
    incidents: 0,
    state: 'Норма',
  },
  {
    id: 'host-354',
    role: 'Площадка А · R-B',
    cpu: 62,
    cpuTrend: [30, 33, 36, 40, 44, 47, 51, 54, 57, 59, 61, 62],
    mem: 73,
    incidents: 1,
    state: 'Норма',
  },
  {
    id: 'host-011',
    role: 'Площадка Б · R-C',
    cpu: 71,
    cpuTrend: [68, 70, 69, 72, 71, 73, 70, 72, 71, 70, 71, 71],
    mem: 81,
    incidents: 4,
    state: 'Проблема',
  },
  {
    id: 'host-006',
    role: 'Площадка В · R-D',
    cpu: 11,
    cpuTrend: [9, 10, 11, 10, 12, 11, 10, 11, 12, 11, 11, 11],
    mem: 29,
    incidents: 0,
    state: 'Норма',
  },
  {
    id: 'host-203',
    role: 'Площадка Г · R-E',
    cpu: 27,
    cpuTrend: [25, 24, 26, 27, 25, 28, 27, 26, 27, 28, 27, 27],
    mem: 52,
    incidents: 0,
    state: 'Норма',
  },
];

/** Узел из образца; адреса других узлов ведут на «не найдено» (R42). */
export const isSampleNode = (id: string) => NODES.some((n) => n.id === id);

/** Номера проблем образца: список нумерует строки с INC-2189, живой поток добавляет новые следом. */
export const INCIDENT_FIRST = 2189;

/** Проблема из образца; INC-9999 и прочие — «не найдено». */
export function isSampleIncident(id: string): boolean {
  const m = /^INC-(\d+)$/.exec(id);
  return m !== null && Number(m[1]) >= INCIDENT_FIRST && Number(m[1]) < INCIDENT_FIRST + 1000;
}

/** Что приезжает в живой список проблем (R35). */
export const ARRIVALS = [
  { name: 'Агент метрик не отвечал 2 мин', target: 'VM 100102', kind: 'vm' as const, sev: 2 },
  { name: 'Потери пакетов 3 % на аплинке', target: 'host-011', kind: 'host' as const, sev: 3 },
  { name: 'Очередь записи растёт на /vds', target: 'host-354', kind: 'host' as const, sev: 3 },
  { name: 'Температура процессора 82 °C', target: 'host-203', kind: 'host' as const, sev: 2 },
];
